import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Import extracted components
import { 
  api,
  CollapsibleSection,
  StatCard,
  SchoolDetailModal,
  MultiSelectDropdown,
  ExportModal,
  // Progress Components
  BulkScrapeProgress,
  SARASEnrichmentProgress,
  UDISEEnrichmentProgress,
  DataMergeProgress,
  GoogleEnrichmentProgress,
  MasterConsolidation,
  GeoPortalImport,
  BulkContactImport,
  // Modal Components
  ScrapeModal,
  PDFUploadModal,
  SchoolFormModal,
  SendProposalModal,
  URLScrapeModal
} from '../components/school-finder';

// Lazy load map component
const SchoolMap = lazy(() => import('../components/school-finder/SchoolMap'));

// Main Page
export default function SchoolFinderPage({ token }) {
  const { language } = useLanguage();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'map'
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const [filters, setFilters] = useState({ state: '', district: '', board: '', permission_level: '', school_type: '', search: '', source: '', has_contact: '', saras_enriched: '', has_whatsapp: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [editSchool, setEditSchool] = useState(null);
  const [proposalSchool, setProposalSchool] = useState(null);
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showURLScrapeModal, setShowURLScrapeModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [enrichingSchool, setEnrichingSchool] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);  // For detail modal

  // Initial data load
  useEffect(() => {
    if (!token || dataLoaded) return;
    
    const loadInitialData = async () => {
      setLoading(true);
      
      // Load schools first (most important)
      try {
        const schoolsRes = await api.get('/api/school-finder/search?page=1&limit=20', token);
        
        if (schoolsRes && schoolsRes.schools) {
          setSchools(schoolsRes.schools);
          setTotal(schoolsRes.total || 0);
          setTotalPages(schoolsRes.total_pages || 1);
        }
        setLoading(false);
      } catch (err) {
        console.error('Schools load error:', err);
        setLoading(false);
      }
      
      // Load states and stats in background (less critical)
      try {
        const [statesRes, statsRes] = await Promise.all([
          api.get('/api/school-finder/states', token),
          api.get('/api/school-finder/comprehensive-stats', token)
        ]);
        
        if (statesRes.success) setStates(statesRes.states || []);
        // Handle both API response formats
        if (statsRes.success) {
          setStats({
            total_schools: statsRes.total || statsRes.total_schools || 0,
            with_any_contact: statsRes.with_any_contact || 0,
            with_phone: statsRes.with_phone || 0,
            whatsapp_ready: statsRes.whatsapp_ready || 0,
            with_email: statsRes.with_email || 0,
            with_location: statsRes.with_gps || statsRes.with_location || 0,
            saras_enriched: statsRes.saras_enriched || 0,
            udise_enriched: statsRes.udise_enriched || 0,
            pending: statsRes.pending || 0,
            states: statsRes.states || []
          });
        }
      } catch (err) {
        console.error('Stats/States load error:', err);
      }
      
      setDataLoaded(true);
    };
    
    loadInitialData();
  }, [token, dataLoaded]);

  // Load schools on filter/page change
  useEffect(() => {
    if (!dataLoaded || !token) return;
    loadSchools();
  }, [filters, page]);
  
  // Load districts on state change
  useEffect(() => {
    if (!dataLoaded || !filters.state) return;
    loadDistricts();
  }, [filters.state]);

  const loadDistricts = async () => {
    if (filters.state) {
      const res = await api.get(`/api/school-finder/districts/${filters.state}`, token);
      if (res.success) setDistricts(res.districts || []);
    }
  };

  const loadStats = async () => {
    // First try quick-stats for instant load
    try {
      const quickRes = await api.get('/api/school-finder/quick-stats', token);
      if (quickRes.success && quickRes.stats) {
        setStats(quickRes.stats);
      }
    } catch (e) {
      console.log('Quick stats not available');
    }
    
    // Then load comprehensive stats in background
    const res = await api.get('/api/school-finder/comprehensive-stats', token);
    if (res.success && res.stats) setStats(res.stats);
    else if (res.total_schools !== undefined) setStats(res);
  };

  const loadSchools = async () => {
    if (!token) return;
    
    setLoading(true);
    
    const params = new URLSearchParams();
    if (filters.state) params.append('state', filters.state);
    if (filters.district) params.append('district', filters.district);
    if (filters.board) params.append('board', filters.board);
    if (filters.permission_level) params.append('permission_level', filters.permission_level);
    if (filters.school_type) params.append('school_type', filters.school_type);
    if (filters.search) params.append('search', filters.search);
    if (filters.source) params.append('source', filters.source);
    if (filters.has_contact) params.append('has_contact', filters.has_contact);
    if (filters.saras_enriched) params.append('saras_enriched', filters.saras_enriched);
    if (filters.has_whatsapp) params.append('has_whatsapp', filters.has_whatsapp);
    params.append('page', page);
    params.append('limit', 20);

    try {
      const res = await api.get(`/api/school-finder/search?${params}`, token);
      if (res && res.schools) {
        setSchools(res.schools);
        setTotal(res.total || 0);
        setTotalPages(res.total_pages || 1);
      }
    } catch (err) {
      console.error('LoadSchools error:', err);
    }
    
    setLoading(false);
  };

  const handleEnrichFromGoogle = async (school) => {
    setEnrichingSchool(school.id || school.affiliation_number);
    const res = await api.post(`/api/school-finder/enrich-from-google/${school.id || school.affiliation_number}`, {}, token);
    if (res.success) {
      alert(res.message_hi || res.message);
      loadSchools();
      loadStats();
    } else {
      alert(res.detail || res.message || 'Error');
    }
    setEnrichingSchool(null);
  };

  const handleSaveSchool = async (formData) => {
    if (editSchool) return await api.put(`/api/school-finder/school/${editSchool.id}`, formData, token);
    return await api.post('/api/school-finder/manual-entry', formData, token);
  };

  const handleDeleteSchool = async (school) => {
    if (!window.confirm('Delete this school?')) return;
    const res = await api.delete(`/api/school-finder/school/${school.id}`, token);
    if (res.success) { loadSchools(); loadStats(); }
  };

  const levelLabels = {
    play_school: 'Play School', primary: 'Primary', middle: 'Middle',
    secondary: 'Secondary (10)', senior_secondary: 'Sr. Sec (12)'
  };

  const sourceLabels = {
    cbse_saras: { label: 'CBSE', color: 'bg-blue-100 text-blue-700' },
    mp_board: { label: 'MP Board', color: 'bg-green-100 text-green-700' },
    udise: { label: 'UDISE', color: 'bg-purple-100 text-purple-700' },
    pdf_upload: { label: 'PDF', color: 'bg-yellow-100 text-yellow-700' },
    scribd_import: { label: 'Scribd', color: 'bg-pink-100 text-pink-700' },
    manual: { label: 'Manual', color: 'bg-orange-100 text-orange-700' }
  };

  const schoolTypeLabels = {
    GOVERNMENT: { label: language === 'hi' ? 'सरकारी' : 'Government', color: 'bg-green-100 text-green-700' },
    PRIVATE: { label: language === 'hi' ? 'प्राइवेट' : 'Private', color: 'bg-blue-100 text-blue-700' },
    AIDED: { label: language === 'hi' ? 'अनुदानित' : 'Aided', color: 'bg-yellow-100 text-yellow-700' }
  };

  return (
    <div className="space-y-4" data-testid="school-finder-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">🔍 School Finder</h1>
          <p className="text-gray-600 text-sm">{language === 'hi' ? 'स्कूलों को खोजें • Data MongoDB में cached है' : 'Find schools • Data cached in MongoDB'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowURLScrapeModal(true)} className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm flex items-center gap-1" data-testid="url-scrape-btn">
            🔗 URL Scrape
          </button>
          <button onClick={() => setShowScrapeModal(true)} className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-1">
            🌐 CBSE Scrape
          </button>
          <button onClick={() => setShowPDFModal(true)} className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm flex items-center gap-1">
            📄 PDF Upload
          </button>
          <button onClick={() => { setEditSchool(null); setShowAddModal(true); }} className="px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm flex items-center gap-1">
            ➕ Add School
          </button>
        </div>
      </div>

      {/* ==================== STATS DASHBOARD ==================== */}
      {stats && (
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 border border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
            📊 {language === 'hi' ? 'डेटाबेस Overview' : 'Database Overview'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Primary Stats */}
            <div className="bg-white rounded-xl p-3 border border-orange-200 shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{(stats.total_schools || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-600">🏫 {language === 'hi' ? 'कुल Schools' : 'Total Schools'}</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-green-200 shadow-sm">
              <div className="text-2xl font-bold text-green-600">{(stats.with_any_contact || stats.with_contact || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-600">✅ {language === 'hi' ? 'Contact है' : 'With Any Contact'}</div>
              <div className="text-xs text-green-500 font-semibold">{stats.contact_percentage || Math.round(((stats.with_any_contact || stats.with_contact)/stats.total_schools)*100)||0}%</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-blue-200 shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{(stats.with_phone || stats.phone_count || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-600">📞 {language === 'hi' ? 'Phone Number' : 'Phone Number'}</div>
              <div className="text-xs text-blue-500 font-semibold">{stats.phone_percentage || Math.round(((stats.with_phone || stats.phone_count)/stats.total_schools)*100)||0}%</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-green-200 shadow-sm">
              <div className="text-2xl font-bold text-green-600">{(stats.whatsapp_ready || stats.whatsapp_count || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-600">💬 WhatsApp Ready</div>
              <div className="text-xs text-green-500 font-semibold">{Math.round(((stats.whatsapp_ready || stats.whatsapp_count)/stats.total_schools)*100)||0}%</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-blue-200 shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{(stats.by_board?.CBSE || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-600">📚 CBSE Schools</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-purple-200 shadow-sm">
              <div className="text-2xl font-bold text-purple-600">{(stats.with_location || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-600">📍 GPS Location</div>
              <div className="text-xs text-purple-500 font-semibold">{stats.location_percentage || Math.round((stats.with_location/stats.total_schools)*100)||0}%</div>
            </div>
          </div>
          
          {/* Enrichment Progress Bar */}
          <div className="mt-4 bg-white rounded-lg p-3 border">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{language === 'hi' ? 'Enrichment Progress' : 'Enrichment Progress'}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>🎓 SARAS</span>
                  <span className="font-semibold">{(stats.saras_enriched || 0).toLocaleString()} / {(stats.by_board?.CBSE || 0).toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{width: `${Math.min(100, Math.round((stats.saras_enriched/(stats.by_board?.CBSE || 1))*100))}%`}}></div>
                </div>
                <div className="text-xs text-purple-600 mt-1">{Math.round((stats.saras_enriched/(stats.by_board?.CBSE || 1))*100)||0}% complete</div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>🏫 UDISE+</span>
                  <span className="font-semibold">{(stats.udise_enriched || 0).toLocaleString()} / {(stats.by_source?.udise_plus || 0).toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{width: `${Math.min(100, Math.round((stats.udise_enriched/(stats.by_source?.udise_plus || 1))*100))}%`}}></div>
                </div>
                <div className="text-xs text-blue-600 mt-1">{Math.round((stats.udise_enriched/(stats.by_source?.udise_plus || 1))*100)||0}% complete</div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>🌐 Geo Portal</span>
                  <span className="font-semibold">{(stats.by_source?.geo_portal || 0).toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{width: '100%'}}></div>
                </div>
                <div className="text-xs text-green-600 mt-1">MP Complete ✅</div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>🔍 Google</span>
                  <span className="font-semibold">{(stats.google_enriched || 0).toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-red-500 h-2 rounded-full" style={{width: `${Math.min(100, Math.round((stats.google_enriched/stats.total_schools)*100))}%`}}></div>
                </div>
                <div className="text-xs text-red-600 mt-1">PAID API</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DATA COLLECTION (FREE) ==================== */}
      <CollapsibleSection 
        title={language === 'hi' ? 'Data Collection (FREE)' : 'Data Collection (FREE)'} 
        icon="📥" 
        defaultOpen={true}
        badge="₹0"
        badgeColor="green"
      >
        <div className="space-y-3">
          {/* Bulk Scrape Progress */}
          <BulkScrapeProgress token={token} onComplete={() => { loadSchools(); loadStats(); }} />

          {/* SARAS Enrichment Progress */}
          <SARASEnrichmentProgress token={token} states={states} />

          {/* UDISE+ Enrichment Progress */}
          <UDISEEnrichmentProgress token={token} />

          {/* Geo Portal Import */}
          <GeoPortalImport token={token} />
        </div>
      </CollapsibleSection>

      {/* ==================== DATA CLEANUP (FREE) ==================== */}
      <CollapsibleSection 
        title={language === 'hi' ? 'Data Cleanup & Merge (FREE)' : 'Data Cleanup & Merge (FREE)'} 
        icon="🔧" 
        defaultOpen={false}
        badge="₹0"
        badgeColor="green"
      >
        <div className="space-y-3">
          {/* Bulk Contact Import - NEW */}
          <BulkContactImport token={token} onComplete={() => { loadStats(); loadSchools(); }} />

          {/* Data Merge Progress */}
          <DataMergeProgress token={token} onComplete={loadStats} />

          {/* Master Data Consolidation */}
          <MasterConsolidation token={token} />
        </div>
      </CollapsibleSection>

      {/* ==================== GOOGLE ENRICHMENT (PAID) ==================== */}
      <CollapsibleSection 
        title={language === 'hi' ? 'Google Enrichment (PAID)' : 'Google Enrichment (PAID)'} 
        icon="🔍" 
        defaultOpen={false}
        badge="₹2.80/school"
        badgeColor="red"
      >
        <div className="bg-yellow-50 rounded-lg p-2 mb-3 text-sm text-yellow-800">
          ⚠️ {language === 'hi' 
            ? 'यह feature Google API use करता है। $0.034 (~₹2.80) per school cost लगती है।' 
            : 'This feature uses Google API. Cost: $0.034 (~₹2.80) per school.'}
        </div>
        <GoogleEnrichmentProgress token={token} />
      </CollapsibleSection>

      {/* ==================== FILTERS & SEARCH ==================== */}
      <div className="bg-white rounded-xl shadow p-3">
        <div className="grid grid-cols-2 md:grid-cols-9 gap-2">
          <select value={filters.state} onChange={(e) => setFilters({...filters, state: e.target.value, district: ''})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-state">
            <option value="">{language === 'hi' ? 'सभी राज्य' : 'All States'}</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.district} onChange={(e) => setFilters({...filters, district: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-district">
            <option value="">{language === 'hi' ? 'सभी जिले' : 'All Districts'}</option>
            {districts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.board} onChange={(e) => setFilters({...filters, board: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-board">
            <option value="">All Boards</option>
            <option value="CBSE">CBSE</option>
            <option value="MP BOARD">MP Board</option>
            <option value="MPSOS">MPSOS</option>
          </select>
          <select value={filters.school_type} onChange={(e) => setFilters({...filters, school_type: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-school-type">
            <option value="">{language === 'hi' ? 'सभी प्रकार' : 'All Types'}</option>
            <option value="GOVERNMENT">{language === 'hi' ? '🏛️ सरकारी' : '🏛️ Government'}</option>
            <option value="PRIVATE">{language === 'hi' ? '🏢 प्राइवेट' : '🏢 Private'}</option>
          </select>
          <select value={filters.permission_level} onChange={(e) => setFilters({...filters, permission_level: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-level">
            <option value="">All Levels</option>
            <option value="play_school">Play School</option>
            <option value="primary">Primary</option>
            <option value="middle">Middle</option>
            <option value="secondary">Secondary</option>
            <option value="senior_secondary">Sr. Secondary</option>
          </select>
          <select value={filters.has_contact} onChange={(e) => setFilters({...filters, has_contact: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-contact">
            <option value="">{language === 'hi' ? 'सभी Contact' : 'All Contact'}</option>
            <option value="yes">{language === 'hi' ? '✅ Contact है' : '✅ Has Contact'}</option>
            <option value="no">{language === 'hi' ? '❌ Contact नहीं' : '❌ No Contact'}</option>
          </select>
          <select value={filters.has_whatsapp} onChange={(e) => setFilters({...filters, has_whatsapp: e.target.value})} className="px-2 py-2 border rounded-lg text-sm border-green-300" data-testid="filter-whatsapp">
            <option value="">{language === 'hi' ? 'सभी WhatsApp' : 'All WhatsApp'}</option>
            <option value="yes">{language === 'hi' ? '💬 WhatsApp है' : '💬 Has WhatsApp'}</option>
            <option value="no">{language === 'hi' ? '❌ WhatsApp नहीं' : '❌ No WhatsApp'}</option>
          </select>
          <select value={filters.saras_enriched} onChange={(e) => setFilters({...filters, saras_enriched: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-enriched">
            <option value="">{language === 'hi' ? 'सभी Enriched' : 'All Enriched'}</option>
            <option value="yes">{language === 'hi' ? '🎓 SARAS Enriched' : '🎓 SARAS Enriched'}</option>
            <option value="udise">{language === 'hi' ? '🏫 UDISE+ Enriched' : '🏫 UDISE+ Enriched'}</option>
            <option value="no">{language === 'hi' ? '⏳ Not Enriched' : '⏳ Not Enriched'}</option>
          </select>
          <select value={filters.source} onChange={(e) => setFilters({...filters, source: e.target.value})} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-source">
            <option value="">All Sources</option>
            <option value="cbse_saras">CBSE SARAS</option>
            <option value="udise_plus">UDISE+</option>
            <option value="pdf_upload">PDF</option>
            <option value="scribd_import">Scribd</option>
            <option value="manual">Manual</option>
            <option value="excel_import">Excel</option>
          </select>
          <input type="text" value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} placeholder={language === 'hi' ? 'खोजें...' : 'Search...'} className="px-2 py-2 border rounded-lg text-sm" data-testid="filter-search" />
        </div>
        {/* Reset Button */}
        <div className="flex justify-end mt-2">
          <button 
            onClick={() => setFilters({ state: '', district: '', board: '', permission_level: '', school_type: '', search: '', source: '', has_contact: '', saras_enriched: '', has_whatsapp: '' })}
            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
          >
            🔄 {language === 'hi' ? 'सभी Filter Reset करें' : 'Reset All Filters'}
          </button>
        </div>
      </div>

      {/* Results Header with View Toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{total} schools found (Page {page}/{totalPages})</p>
        <div className="flex gap-2 items-center">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded text-sm transition-colors ${viewMode === 'table' ? 'bg-white shadow text-orange-600' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid="view-table-btn"
            >
              📋 {language === 'hi' ? 'Table' : 'Table'}
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-1 rounded text-sm transition-colors ${viewMode === 'map' ? 'bg-white shadow text-teal-600' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid="view-map-btn"
            >
              🗺️ {language === 'hi' ? 'Map' : 'Map'}
            </button>
          </div>
          <button 
            onClick={() => setShowExportModal(true)}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-1"
            data-testid="export-excel-btn"
          >
            📥 {language === 'hi' ? 'Export (Column Select)' : 'Export (Column Select)'}
          </button>
        </div>
      </div>

      {/* Error Banner with Retry */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-700">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
          <button
            onClick={() => { setError(null); loadSchools(); loadStats(); }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            data-testid="retry-btn"
          >
            🔄 {language === 'hi' ? 'फिर से कोशिश करें' : 'Retry'}
          </button>
        </div>
      )}

      {/* Map View */}
      {viewMode === 'map' && (
        <Suspense fallback={<div className="bg-white rounded-xl shadow p-8 text-center">🗺️ Loading Map...</div>}>
          <SchoolMap 
            token={token} 
            language={language} 
            filters={filters}
            onSchoolSelect={(school) => setSelectedSchool(school)}
          />
        </Suspense>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{language === 'hi' ? 'स्कूल का नाम' : 'School Name'}</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600">{language === 'hi' ? 'जिला' : 'District'}</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600">{language === 'hi' ? 'प्रकार' : 'Type'}</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600">{language === 'hi' ? 'बोर्ड' : 'Board'}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{language === 'hi' ? 'संपर्क व्यक्ति' : 'Contact Person'}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{language === 'hi' ? 'संपर्क' : 'Contact'}</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600">{language === 'hi' ? 'स्रोत' : 'Source'}</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600">{language === 'hi' ? 'कार्यवाही' : 'Action'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div></td></tr>
              ) : schools.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <span className="text-gray-500">🔍 {language === 'hi' ? 'कोई स्कूल नहीं मिला' : 'No schools found'}</span>
                    {total === 0 && !filters.state && !filters.search && (
                      <button 
                        onClick={() => { loadSchools(); loadStats(); }}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
                        data-testid="reload-schools-btn"
                      >
                        🔄 {language === 'hi' ? 'रीलोड करें' : 'Reload'}
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : schools.map((school) => {
                // Clean school name - remove codes and extra prefixes
                const cleanSchoolName = (name) => {
                  if (!name) return '';
                  return name
                    .replace(/^\(\d+\)\s*/g, '')  // Remove (01004) prefix
                    .replace(/^\(\+\d+\)\s*/g, '') // Remove (+2) prefix
                    .replace(/^\d+[-\s]+/g, '')   // Remove number prefixes like "01004-"
                    .replace(/\s*\([A-Z]\)$/g, '') // Remove (V), (M) suffixes
                    .replace(/\s*\d+-No$/gi, '')  // Remove "2-No" suffix
                    .replace(/\s+/g, ' ')         // Clean multiple spaces
                    .trim();
                };
                
                // Determine school type based on name pattern if not set
                const getSchoolType = () => {
                  if (school.school_type) return school.school_type;
                  const name = (school.school_name || '').toUpperCase();
                  if (name.match(/^(GOVT|GOVERNMENT|KENDRIYA|JAWAHAR|NAVODAYA|EKLAVYA|SAINIK)/)) return 'GOVERNMENT';
                  return 'PRIVATE';
                };
                const schoolType = getSchoolType();
                
                return (
                <tr 
                  key={school.id || school.affiliation_number || school.udise_code || Math.random()} 
                  className="hover:bg-orange-50 cursor-pointer transition-colors" 
                  data-testid={`school-row-${school.id || school.affiliation_number}`}
                  onClick={() => setSelectedSchool(school)}
                >
                  {/* School Name - Clean display */}
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-800 text-sm hover:text-orange-600">
                      {cleanSchoolName(school.school_name)}
                    </p>
                  </td>
                  {/* District */}
                  <td className="px-3 py-2 text-center text-xs">{school.district || '-'}</td>
                  {/* Type - Private/Government */}
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${schoolType === 'GOVERNMENT' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {schoolType === 'GOVERNMENT' ? (language === 'hi' ? '🏛️ सरकारी' : '🏛️ Govt') : (language === 'hi' ? '🏢 प्राइवेट' : '🏢 Pvt')}
                    </span>
                  </td>
                  {/* Board */}
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${school.board === 'CBSE' ? 'bg-blue-100 text-blue-700' : school.board === 'MP BOARD' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                      {school.board}
                    </span>
                  </td>
                  {/* Contact Person Name + Designation */}
                  <td className="px-3 py-2">
                    <p className="text-sm text-gray-800">
                      {school.contact_person_name || school.principal_name || '-'}
                    </p>
                    {(school.contact_person_designation || school.principal_name) && (
                      <span className="text-xs text-purple-600">
                        {school.contact_person_designation || 'Principal'}
                        {school.principal_qualification && <span className="text-gray-400"> • {school.principal_qualification}</span>}
                      </span>
                    )}
                  </td>
                  {/* Contact - WhatsApp, Mobile, Landline, Email */}
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    {school.has_contact !== false && (school.whatsapp_number || school.mobile_number || school.phone || school.email) ? (
                      <div className="text-xs space-y-1">
                        {school.whatsapp_number && (
                          <a href={`https://wa.me/91${school.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="text-green-600 flex items-center gap-1 hover:underline">
                            <span className="bg-green-100 px-1 rounded">💬</span> {school.whatsapp_number}
                          </a>
                        )}
                        {school.mobile_number && school.mobile_number !== school.whatsapp_number && (
                          <a href={`tel:${school.mobile_number}`} className="text-blue-600 flex items-center gap-1">
                            <span className="bg-blue-100 px-1 rounded">📱</span> {school.mobile_number}
                          </a>
                        )}
                        {!school.whatsapp_number && !school.mobile_number && school.phone && (
                          <a href={`tel:${school.phone}`} className="text-blue-600 flex items-center gap-1">
                            <span className="bg-blue-100 px-1 rounded">📞</span> {school.phone}
                          </a>
                        )}
                        {school.landline_number && (
                          <span className="text-gray-500 flex items-center gap-1">
                            <span className="bg-gray-100 px-1 rounded">☎️</span> {school.landline_number}
                          </span>
                        )}
                        {school.email && (
                          <a href={`mailto:${school.email}`} className="text-purple-600 flex items-center gap-1 truncate" style={{maxWidth: '140px'}} title={school.email}>
                            <span className="bg-purple-100 px-1 rounded">📧</span> {school.email}
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">{language === 'hi' ? 'नहीं' : 'N/A'}</span>
                        <button
                          onClick={() => handleEnrichFromGoogle(school)}
                          disabled={enrichingSchool === (school.id || school.affiliation_number)}
                          className="block mt-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs hover:bg-yellow-200 disabled:opacity-50 mx-auto"
                          title="Get from Google"
                          data-testid={`enrich-google-btn-${school.id || school.affiliation_number}`}
                        >
                          {enrichingSchool === (school.id || school.affiliation_number) ? '⏳' : '📍'}
                        </button>
                      </div>
                    )}
                  </td>
                  {/* Source */}
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${sourceLabels[school.source]?.color || 'bg-gray-100'}`}>
                      {sourceLabels[school.source]?.label || school.source}
                    </span>
                  </td>
                  {/* Actions */}
                  <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setProposalSchool(school)} className="px-2 py-1 bg-green-500 text-white rounded text-xs" title="Send Proposal" data-testid={`send-proposal-btn-${school.id || school.affiliation_number}`}>📤</button>
                      <button onClick={() => { setEditSchool(school); setShowAddModal(true); }} className="px-2 py-1 bg-blue-500 text-white rounded text-xs" title="Edit" data-testid={`edit-school-btn-${school.id || school.affiliation_number}`}>✏️</button>
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-3 border-t">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1 rounded border disabled:opacity-50">←</button>
            <span className="text-sm">Page {page}/{totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-3 py-1 rounded border disabled:opacity-50">→</button>
          </div>
        )}
      </div>
      )}

      {/* Modals */}
      <ScrapeModal isOpen={showScrapeModal} onClose={() => setShowScrapeModal(false)} token={token} state={filters.state} district={filters.district} onComplete={() => { loadSchools(); loadStats(); }} />
      <PDFUploadModal isOpen={showPDFModal} onClose={() => setShowPDFModal(false)} token={token} states={states} onComplete={() => { loadSchools(); loadStats(); }} />
      <SchoolFormModal isOpen={showAddModal} onClose={() => { setShowAddModal(false); setEditSchool(null); }} school={editSchool} onSave={async (data) => { const r = await handleSaveSchool(data); if (r.success) { loadSchools(); loadStats(); } return r; }} states={states} token={token} />
      <SendProposalModal isOpen={!!proposalSchool} onClose={() => setProposalSchool(null)} school={proposalSchool} token={token} />
      <URLScrapeModal isOpen={showURLScrapeModal} onClose={() => setShowURLScrapeModal(false)} token={token} onSuccess={() => { loadSchools(); loadStats(); }} />
      
      {/* Export Modal with Column Selection */}
      {showExportModal && (
        <ExportModal 
          token={token} 
          onClose={() => setShowExportModal(false)} 
          filters={filters}
          language={language}
          states={states}
        />
      )}
      
      {/* School Detail Modal */}
      {selectedSchool && (
        <SchoolDetailModal 
          school={selectedSchool} 
          onClose={() => setSelectedSchool(null)} 
          onEdit={(school) => { setEditSchool(school); setShowAddModal(true); }}
          language={language}
          token={token}
          onRefresh={async () => {
            // Reload schools list
            await loadSchools();
          }}
        />
      )}
    </div>
  );
}
